import { Card } from "@/components/ui/card";
import { CheckCircle2, ClipboardList } from "lucide-react";
import type { NHSHypertensionABPMFollowUpFormData } from "./NHSHypertensionABPMFollowUpForm";

interface Props {
  isDarkMode?: boolean;
  data: NHSHypertensionABPMFollowUpFormData;
}

const diagnosisMap: Record<string, string> = {
  "no-hypertension": "No hypertension",
  "white-coat": "White-coat hypertension",
  "stage-1-hypertension": "Stage 1 hypertension",
  "stage-2-hypertension": "Stage 2 hypertension",
  "severe-hypertension": "Severe hypertension",
  inconclusive: "Inconclusive – repeat required",
};

const outcomeMap: Record<string, string> = {
  "discharge-no-action": "Discharge – no further action",
  "lifestyle-advice": "Lifestyle advice given – discharge",
  "repeat-abpm": "Repeat ABPM required",
  "refer-gp-routine": "Referred to GP (routine)",
  "refer-gp-urgent": "Referred to GP (urgent / same day)",
};

const formatDate = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Not specified";

export function NHSHypertensionABPMFollowUpSummary({
  isDarkMode = false,
  data,
}: Props) {
  const cardCls = `p-4 rounded-2xl border ${
    isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
  }`;
  const titleCls = `text-sm font-semibold ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const labelCls = `text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
  const valueCls = `text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`;

  const fmtAvg = (s: string, d: string) => (s && d ? `${s}/${d} mmHg` : "—");

  return (
    <div className="space-y-4">
      <div
        className={`flex items-start gap-3 p-4 rounded-2xl ${
          isDarkMode
            ? "bg-emerald-900/20 border border-emerald-800"
            : "bg-emerald-50 border border-emerald-200"
        }`}
      >
        <CheckCircle2
          className={`w-5 h-5 mt-0.5 ${
            isDarkMode ? "text-emerald-300" : "text-emerald-600"
          }`}
        />
        <div>
          <div
            className={`text-sm font-semibold ${
              isDarkMode ? "text-emerald-200" : "text-emerald-900"
            }`}
          >
            Stage 3 – ABPM Follow Up Completed
          </div>
          <div className={labelCls}>
            {formatDate(data.consultationDate)} · {data.consultationTime || "—"}
          </div>
        </div>
      </div>

      <Card className={cardCls}>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList
            className={`w-4 h-4 ${
              isDarkMode ? "text-rose-300" : "text-rose-600"
            }`}
          />
          <h4 className={titleCls}>ABPM Results</h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className={labelCls}>Total / successful</div>
            <div className={valueCls}>
              {data.successfulReadings || "—"} / {data.totalReadings || "—"}
            </div>
          </div>
          <div>
            <div className={labelCls}>Daytime average</div>
            <div className={`${valueCls} font-semibold`}>
              {fmtAvg(data.daytimeAvgSystolic, data.daytimeAvgDiastolic)}
            </div>
          </div>
          <div>
            <div className={labelCls}>Night-time average</div>
            <div className={valueCls}>
              {fmtAvg(data.nighttimeAvgSystolic, data.nighttimeAvgDiastolic)}
            </div>
          </div>
          <div>
            <div className={labelCls}>24-hour average</div>
            <div className={valueCls}>
              {fmtAvg(
                data.twentyFourHourAvgSystolic,
                data.twentyFourHourAvgDiastolic,
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Diagnosis</h4>
        <div className="mt-3 space-y-2">
          <div>
            <div className={labelCls}>Diagnosis</div>
            <div className={`${valueCls} font-semibold`}>
              {diagnosisMap[data.diagnosis] || data.diagnosis || "—"}
            </div>
          </div>
          {data.diagnosisNotes && (
            <div>
              <div className={labelCls}>Notes</div>
              <div className={valueCls}>{data.diagnosisNotes}</div>
            </div>
          )}
        </div>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Outcome</h4>
        <div className="mt-3 space-y-2">
          <div>
            <div className={labelCls}>Decision</div>
            <div className={valueCls}>
              {outcomeMap[data.outcome] || data.outcome || "—"}
            </div>
          </div>
          {data.referralRecipient && (
            <div>
              <div className={labelCls}>Referral recipient</div>
              <div className={valueCls}>{data.referralRecipient}</div>
            </div>
          )}
          {data.outcomeNotes && (
            <div>
              <div className={labelCls}>Notes</div>
              <div className={valueCls}>{data.outcomeNotes}</div>
            </div>
          )}
        </div>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Patient Communication</h4>
        <ul className={`${valueCls} mt-2 space-y-1 list-disc pl-5`}>
          <li>Results explained: {data.resultsExplained ? "Yes" : "No"}</li>
          <li>
            Lifestyle / follow-up advice:{" "}
            {data.followUpAdviceGiven ? "Given" : "Not given"}
          </li>
          <li>
            Patient leaflet: {data.patientLeafletGiven ? "Given" : "Not given"}
          </li>
        </ul>
      </Card>

      {data.internalNotes && (
        <Card className={cardCls}>
          <h4 className={titleCls}>Internal Notes</h4>
          <div className={`${valueCls} mt-2 whitespace-pre-wrap`}>
            {data.internalNotes}
          </div>
        </Card>
      )}
    </div>
  );
}
